import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiCheckCircleLight, PiWarningCircleLight, PiFileTextLight } from 'react-icons/pi';
import FileIcon from '../../attachments/fileIcon';
import Spinner from '../../TailwindComponents/Spinner';
import AIProgressBar from './AIProgressBar';
import { AISubItem } from './aiRun';

/**
 * Per-item sub-progress - one row per file, document or record the run is
 * working through. Rows are `Well`-style sunken boxes inside the glass card,
 * which is what keeps them readable on a translucent surface.
 */

const SubItemRow: React.FC<{ item: AISubItem; showFileIcon: boolean }> = ({
  item,
  showFileIcon,
}) => {
  const { t } = useTranslation();
  const settled = item.status === 'completed' || item.status === 'failed';

  return (
    <li
      className="rounded-md bg-surface-sunken/70 border border-hairline-soft px-2.5 py-2"
      data-testid="ai-sub-item"
      data-status={item.status}
    >
      <div className="flex items-center gap-2">
        {showFileIcon ? (
          <FileIcon name={item.name} className="h-4 w-4 shrink-0" />
        ) : (
          <PiFileTextLight className="h-4 w-4 shrink-0 text-fg-muted" />
        )}
        <span className="flex-1 min-w-0 text-xs text-fg-body truncate" title={item.name}>
          {item.name}
        </span>
        {item.status === 'failed' ? (
          <PiWarningCircleLight className="w-4 h-4 text-danger-500 shrink-0" />
        ) : item.status === 'completed' ? (
          <PiCheckCircleLight className="w-4 h-4 text-success-500 shrink-0" />
        ) : item.percent === null ? (
          /* No number to print for this item yet. The word "Waiting" sat there
             static for the whole run and read as a stall; the app's own
             spinner says the same thing and says it is still moving. A
             finished item keeps its check - only the unsettled ones spin. */
          <Spinner
            size="glyph"
            tone="muted"
            className="shrink-0"
            ariaLabel={item.status === 'pending' ? t('Waiting') : t('In progress')}
          />
        ) : (
          <span className="text-[11px] text-fg-muted shrink-0 tabular-nums">
            {`${item.percent}%`}
          </span>
        )}
      </div>
      {!settled && (
        <AIProgressBar
          percent={item.status === 'pending' ? 0 : item.percent}
          tone={item.status === 'pending' ? 'muted' : 'champagne'}
          className="mt-1.5 h-1"
        />
      )}
      {item.message && (
        <p className="mt-1 text-[11px] text-fg-muted break-words">{item.message}</p>
      )}
    </li>
  );
};

export interface AISubItemListProps {
  items: AISubItem[];
  /** Header line. Defaults to the document count. */
  label?: string;
  /** Items are filenames - render the per-extension icon. Default true. */
  fileIcons?: boolean;
  className?: string;
}

const AISubItemList: React.FC<AISubItemListProps> = ({
  items,
  label,
  fileIcons = true,
  className,
}) => {
  const { t } = useTranslation();
  if (!items.length) return null;

  return (
    <div className={clsx('space-y-2', className)}>
      <p className="text-[11px] uppercase tracking-wide text-fg-muted flex items-center gap-1.5">
        <PiFileTextLight className="w-3.5 h-3.5" />
        {label ??
          (items.length === 1 ? t('1 document') : t('{{count}} documents', { count: items.length }))}
      </p>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <SubItemRow key={item.id} item={item} showFileIcon={fileIcons} />
        ))}
      </ul>
    </div>
  );
};

export default AISubItemList;
